import {
  Button,
  CardBackground,
  NumberField,
  TextField,
  Typography,
  useToast,
} from "@hellocarbo/lasagne";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  redirectDocument,
  useActionData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import PageWrapper from "@/components/ui/PageWrapper";

import { useExternalCampaignContext } from "./ExternalCampaign";

export async function action({ request, params }: ActionFunctionArgs) {
  const { externalCampaignId } = params;
  const formData = await request.formData();
  const externalProvider = formData.get("externalProvider");
  const employees = Number(formData.get("employees"));
  const turnover = Number(formData.get("turnover"));

  const response = await fetch(
    `/api/client/external_campaigns/${externalCampaignId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        employees,
        turnover,
        score: {
          external_provider: externalProvider,
        },
      }),
    }
  );

  if (response.status !== 200) {
    const errorData = (await response.json()) as { code: string };

    if (errorData?.code === "period_intersects_existing_tracking_campaign") {
      return {
        error: "externalCampaign:error.periodsIntersect",
      };
    }

    return { toastError: "externalCampaign:error:generic" };
  }

  return redirectDocument(
    `/tracking/external_campaigns/${externalCampaignId}/scopes`
  );
}

function EditExternalCampaign() {
  const { t } = useTranslation("externalCampaign");
  const { toast } = useToast();
  const navigation = useNavigation();
  const [error, setError] = useState<string>();

  const { externalCampaign } = useExternalCampaignContext();
  const actionData = useActionData<typeof action>();

  invariant(
    externalCampaign,
    "Unable to access information from the external campaign."
  );

  useEffect(() => {
    // Show generic error message in negative toast
    if (navigation.state === "idle" && actionData?.toastError) {
      toast({
        title: t(actionData.toastError),
        intent: "negative",
      });
    }
  }, [actionData?.toastError, navigation.state, t, toast]);

  useEffect(() => {
    // Reset error when the period view is not displayed
    if (error) {
      setError(undefined);
    }

    // Set an error when the period view is displayed and the action has been submitted
    if (navigation.state === "loading" && actionData?.error) {
      setError(actionData.error);
    }
  }, [actionData?.error, navigation.state, error]);

  return (
    <PageWrapper>
      <div className="my-lg grid grid-cols-4">
        <CardBackground className="col-start-2 col-end-4">
          <Typography as="h1" step="2" weight="bold" className="mb-xs">
            {t("externalCampaign:edit.title")}
          </Typography>
          <Form method="PATCH">
            <div className="gap-y-xs flex flex-col">
              <TextField
                name="externalProvider"
                label={t("externalCampaign:externalProvider.label")}
                defaultValue={externalCampaign.score.external_provider}
                placeholder={t("externalCampaign:externalProvider.placeholder")}
                fullWidth
              />
              <NumberField
                name="turnover"
                label={t("externalCampaign:employeesTurnover.turnover.label")}
                defaultValue={externalCampaign.turnover}
                fullWidth
                unit={t("common:unit.eurosPerYear")}
              />
              <NumberField
                name="employees"
                label={t("externalCampaign:employeesTurnover.employees.label")}
                defaultValue={externalCampaign.employees}
                fullWidth
              />
            </div>
            <div className="mt-lg text-center">
              <Button type="submit" isLoading={navigation.state !== "idle"}>
                {t("common:validate")}
              </Button>
            </div>
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default EditExternalCampaign;
